import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
    getCompanies,
    getCompanyView,
    getCompanyNews,
    getCompanyResearchReport,
    getAusIqCompanySummary,
    getAusIqIqCash,
    getAusIqMiningProjects,
    getAusIqOptions
} from './companyAPI'

export const getCompaniesAsync = createAsyncThunk(
    'companies/getCompanies',
    async (payload) => {
        const response = payload === undefined ? await getCompanies() : payload;
        return response;
    }
);

export const getCurrentCompanyAsync = createAsyncThunk(
    'companies/currentCompany',
    async (slug) => {
        try {
            const response = await getCompanyView(slug);
            return response;
        } catch (e) {
            // trigger event for livewire
            var notFound = new CustomEvent('notFound', { detail: { method: 'companies/currentCompany', slug: slug } });
            window.dispatchEvent(notFound);
            throw new Error(e);
        }
    }
);

export const getCurrentCompanyNewsAsync = createAsyncThunk(
    'companies/currentCompanyNews',
    async ({slug, page}) => {
        const response = await getCompanyNews(slug, { params: { page } });
        return response;
    }
);

export const getCurrentCompanyResearchReportAsync = createAsyncThunk(
    'companies/currentCompanyResearchReport',
    async ({slug, researchSlug}) => {
        console.log('slug', slug);
        console.log('researchSlug', researchSlug);
        const response = await getCompanyResearchReport(slug, researchSlug);
        return response;
    }
);

export const getCurrentCompanyAusIqSummaryAsync = createAsyncThunk(
    'companies/currentCompanyAusIqSummary',
    async (ticker) => {
        const response = await getAusIqCompanySummary(ticker);
        return response;
    }
);

export const getCurrentCompanyAusIqIqCashAsync = createAsyncThunk(
    'companies/currentCompanyAusIqIqCash',
    async (ticker) => {
        const response = await getAusIqIqCash(ticker);
        return response;
    }
);

export const getCurrentCompanyAusIqMiningProjectsAsync = createAsyncThunk(
    'companies/currentCompanyAusIqMiningProjects',
    async (ticker) => {
        const response = await getAusIqMiningProjects(ticker);
        return response;
    }
);

export const getCurrentCompanyAusIqOptionsAsync = createAsyncThunk(
    'companies/currentCompanyAusIqOptions',
    async (ticker) => {
        const response = await getAusIqOptions(ticker);
        return response;
    }
);

const initialState = {
    companies: {
        status: 'idle',
        results: [],
    },
    stats: {
        counts: {
            active_companies: 0
        }
    },
    currentCompany: null,
    currentCompanyStatus: 'idle',
    currentCompanyExtras: {
        currentCompanyNews: [],
        currentCompanyNewsStatus: 'idle',
        currentResearchReport: null,
        currentResearchReportStatus: 'idle',
        ausIq: {
            summary: null,
            summaryStatus: 'idle',
            iqCash: null,
            iqCashStatus: 'idle',
            miningProjects: null,
            miningProjectsStatus: 'idle',
            options: null,
            optionsStatus: 'idle',
        }
    },
};

const companies = createSlice({
    name: 'companies',
    initialState,
    reducers: {

    },
    extraReducers: (builder) => {
        builder.addCase(getCompaniesAsync.pending, (state, { payload }) => {
            state.companies.status = 'loading';
        })
            .addCase(getCompaniesAsync.rejected, (state) => {
                state.companies.results = [];
                state.companies.meta = [];
                state.companies.status = 'attempted';
            })
            .addCase(getCompaniesAsync.fulfilled, (state, { payload }) => {
                state.companies.results = payload?.data ?? [];
                state.companies.meta = payload?.meta ?? [];
                state.stats.counts = {
                    active_companies: payload?.meta?.total ?? 0
                }
                state.companies.status = 'attempted';
            })
            .addCase(getCurrentCompanyAsync.pending, (state, { payload }) => {
                state.currentCompanyStatus = 'loading';
            })
            .addCase(getCurrentCompanyAsync.rejected, (state, { payload }) => {
                state.currentCompany = null;
                state.currentCompanyStatus = 'attempted';
            })
            .addCase(getCurrentCompanyAsync.fulfilled, (state, { payload }) => {
                state.currentCompany = payload?.data;
                state.currentCompanyStatus = 'attempted';
            })
            .addCase(getCurrentCompanyNewsAsync.pending, (state, { payload }) => {
                state.currentCompanyExtras.currentCompanyNewsStatus = 'loading';
            })
            .addCase(getCurrentCompanyNewsAsync.rejected, (state, { payload }) => {
                state.currentCompanyExtras.currentCompanyNews = [];
                state.currentCompanyExtras.currentCompanyNewsStatus = 'attempted';
            })
            .addCase(getCurrentCompanyNewsAsync.fulfilled, (state, { payload }) => {
                state.currentCompanyExtras.currentCompanyNews = payload?.data;
                state.currentCompanyExtras.currentCompanyNewsStatus = 'attempted';
            })
            .addCase(getCurrentCompanyResearchReportAsync.pending, (state, { payload }) => {
                state.currentCompanyExtras.currentResearchReportStatus = 'loading';
            })
            .addCase(getCurrentCompanyResearchReportAsync.rejected, (state, { payload }) => {
                state.currentCompanyExtras.currentResearchReport = null;
                state.currentCompanyExtras.currentResearchReportStatus = 'attempted';
            })
            .addCase(getCurrentCompanyResearchReportAsync.fulfilled, (state, { payload }) => {
                state.currentCompanyExtras.currentResearchReport = payload?.data;
                state.currentCompanyExtras.currentResearchReportStatus = 'attempted';
            })
            .addCase(getCurrentCompanyAusIqSummaryAsync.pending, (state, { payload }) => {
                state.currentCompanyExtras.ausIq.summaryStatus = 'loading';
            })
            .addCase(getCurrentCompanyAusIqSummaryAsync.rejected, (state, { payload }) => {
                state.currentCompanyExtras.ausIq.summary = null;
                state.currentCompanyExtras.ausIq.summaryStatus = 'attempted';
            })
            .addCase(getCurrentCompanyAusIqSummaryAsync.fulfilled, (state, { payload }) => {
                state.currentCompanyExtras.ausIq.summary = payload?.data;
                state.currentCompanyExtras.ausIq.summaryStatus = 'attempted';
            })
            .addCase(getCurrentCompanyAusIqIqCashAsync.pending, (state, { payload }) => {
                state.currentCompanyExtras.ausIq.iqCashStatus = 'loading';
            })
            .addCase(getCurrentCompanyAusIqIqCashAsync.rejected, (state, { payload }) => {
                state.currentCompanyExtras.ausIq.iqCash = null;
                state.currentCompanyExtras.ausIq.iqCashStatus = 'attempted';
            })
            .addCase(getCurrentCompanyAusIqIqCashAsync.fulfilled, (state, { payload }) => {
                state.currentCompanyExtras.ausIq.iqCash = payload?.data;
                state.currentCompanyExtras.ausIq.iqCashStatus = 'attempted';
            })
            .addCase(getCurrentCompanyAusIqMiningProjectsAsync.pending, (state, { payload }) => {
                state.currentCompanyExtras.ausIq.miningProjectsStatus = 'loading';
            })
            .addCase(getCurrentCompanyAusIqMiningProjectsAsync.rejected, (state, { payload }) => {
                state.currentCompanyExtras.ausIq.miningProjects = null;
                state.currentCompanyExtras.ausIq.miningProjectsStatus = 'attempted';
            })
            .addCase(getCurrentCompanyAusIqMiningProjectsAsync.fulfilled, (state, { payload }) => {
                state.currentCompanyExtras.ausIq.miningProjects = payload?.data;
                state.currentCompanyExtras.ausIq.miningProjectsStatus = 'attempted';
            })
            .addCase(getCurrentCompanyAusIqOptionsAsync.pending, (state, { payload }) => {
                state.currentCompanyExtras.ausIq.optionsStatus = 'loading';
            })
            .addCase(getCurrentCompanyAusIqOptionsAsync.rejected, (state, { payload }) => {
                state.currentCompanyExtras.ausIq.options = null;
                state.currentCompanyExtras.ausIq.optionsStatus = 'attempted';
            })
            .addCase(getCurrentCompanyAusIqOptionsAsync.fulfilled, (state, { payload }) => {
                state.currentCompanyExtras.ausIq.options = payload?.data;
                state.currentCompanyExtras.ausIq.optionsStatus = 'attempted';
            });
    },
});

export {
    getCompanies,
    getCompanyView,
    getCompanyNews,
    getCompanyResearchReport,
    getAusIqCompanySummary,
    getAusIqIqCash,
    getAusIqMiningProjects,
    getAusIqOptions
}
export default companies.reducer;

